import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PRICE_LIST_BASE_LABELS, PRICE_LISTS_BASE_ORDER, type PriceListBase, type PriceListDTO, type PriceListRowDTO } from '@bifeai/shared';
import { Button, Callout, Card, CardBody, CardHeader, Dialog, EmptyState, Field, Input, PageHeader, Select, Skeleton, useToast } from '@bifeai/ui';
import { Plus, Tags } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { SearchBox, Toolbar, ViewToggle } from '../../components/inventory';
import { useViewMode } from '../../lib/view-mode';
import { moneyInput, parseMoney, usePaged, usePriceLists } from './common';
import './wholesale.css';

/** Listas de precios del mayorista: propias o calculadas (sobre otra lista o el precio del mostrador). */
export function PriceListsPage() {
  const lists = usePriceLists();
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<PriceListDTO | 'new' | null>(null);
  const current = (lists.data ?? []).find((l) => l.id === selected) ?? lists.data?.[0] ?? null;
  return (
    <>
      <PageHeader
        title="Listas de precios"
        subtitle="Una lista por tipo de cliente, zona o vendedor. Las calculadas se actualizan solas cuando cambia su base."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
            Nueva lista
          </Button>
        }
      />
      {lists.isLoading ? (
        <Skeleton style={{ height: 300 }} />
      ) : !lists.data?.length ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<Tags />}
              title="Todavía no hay listas"
              text="Armá la lista mayorista general y, si querés, otras calculadas sobre ella (por ejemplo, Restaurantes: 5 % menos)."
            />
          </CardBody>
        </Card>
      ) : (
        <div className="bf-ws-grid">
          <div className="bf-ws-list">
            {lists.data.map((l) => (
              <button key={l.id} type="button" className="bf-pick" aria-current={current?.id === l.id} onClick={() => setSelected(l.id)}>
                <strong>{l.name}</strong>
                <span className="bf-cell-sub">
                  {l.baseKind === 'OWN'
                    ? `${l.ownPrices} precios`
                    : `${l.adjustPct > 0 ? '+' : ''}${l.adjustPct} % sobre ${l.baseKind === 'RETAIL' ? 'el mostrador' : l.baseListName}`}
                </span>
              </button>
            ))}
          </div>
          {current && <ListDetail list={current} onEdit={() => setEditing(current)} />}
        </div>
      )}
      {editing && (
        <ListDialog
          list={editing === 'new' ? null : editing}
          all={lists.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(id) => (setEditing(null), id && setSelected(id))}
        />
      )}
    </>
  );
}

type Show = '' | 'own' | 'calc' | 'none' | 'negative';

function ListDetail({ list, onEdit }: { list: PriceListDTO; onEdit: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const rows = useQuery({
    queryKey: ['wholesale', 'price-list', list.id],
    queryFn: () => api.get<PriceListRowDTO[]>(`/wholesale/price-lists/${list.id}/items`),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [find, setFind] = useState('');
  const [category, setCategory] = useState('');
  const [show, setShow] = useState<Show>('');
  const [view, setView] = useViewMode('mayorista-listas', 'list');
  const costs = can('costs.view');
  const categories = useMemo(
    () => [...new Set((rows.data ?? []).map((r) => r.categoryName ?? 'Sin categoría'))].sort((x, y) => x.localeCompare(y, 'es')),
    [rows.data],
  );
  const shown = useMemo(() => {
    const t = find.trim().toLowerCase();
    return (rows.data ?? []).filter(
      (r) =>
        (!t || r.productName.toLowerCase().includes(t)) &&
        (!category || (r.categoryName ?? 'Sin categoría') === category) &&
        (show === '' ||
          (show === 'own' && r.ownPriceCents !== null) ||
          (show === 'calc' && r.ownPriceCents === null && r.priceCents !== null) ||
          (show === 'none' && r.priceCents === null) ||
          (show === 'negative' && r.marginPct !== null && r.marginPct < 0)),
    );
  }, [rows.data, find, category, show]);
  const { page, pager } = usePaged(shown, view === 'grid' ? 12 : 20, [find, category, show, view, list.id].join('|'));
  const changed = Object.keys(draft);
  const withPrice = (rows.data ?? []).filter((r) => r.priceCents !== null).length;
  const save = useMutation({
    mutationFn: () =>
      api.put<PriceListRowDTO[]>(`/wholesale/price-lists/${list.id}/items`, {
        items: changed.map((productId) => ({ productId, priceCents: draft[productId]!.trim() ? parseMoney(draft[productId]!) : null })),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      setDraft({});
      toast.success('Precios guardados');
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const priceInput = (r: PriceListRowDTO) => (
    <Input
      inputMode="decimal"
      placeholder={list.baseKind === 'OWN' ? 'Sin precio' : 'Calculado'}
      value={draft[r.productId] ?? moneyInput(r.ownPriceCents)}
      onChange={(e) => setDraft({ ...draft, [r.productId]: e.target.value })}
      aria-label={`Precio de ${r.productName}`}
    />
  );
  const margin = (r: PriceListRowDTO) =>
    r.marginPct !== null ? (
      <span style={{ color: r.marginPct < 0 ? 'var(--bf-danger)' : undefined }}>{`${r.marginPct.toLocaleString('es-AR')} %`}</span>
    ) : (
      '—'
    );
  return (
    <Card>
      <CardHeader
        title={list.name}
        subtitle={`${
          list.baseKind === 'OWN'
            ? PRICE_LIST_BASE_LABELS.OWN
            : `${list.adjustPct > 0 ? '+' : ''}${list.adjustPct} % sobre ${list.baseKind === 'RETAIL' ? 'el precio del mostrador' : `la lista ${list.baseListName}`}. Un precio propio pisa el cálculo`
        } · ${withPrice} de ${rows.data?.length ?? 0} productos con precio.`}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onEdit}>Editar lista</Button>
            <Button variant="primary" disabled={!changed.length} loading={save.isPending} onClick={() => save.mutate()}>
              Guardar {changed.length ? `(${changed.length})` : ''}
            </Button>
          </div>
        }
      />
      <CardBody>
        <Toolbar>
          <SearchBox value={find} onChange={setFind} placeholder="Buscar producto" />
          <Select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Categoría">
            <option value="">Todas las categorías</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select value={show} onChange={(e) => setShow(e.target.value as Show)} aria-label="Mostrar">
            <option value="">Todos los productos</option>
            <option value="own">Con precio propio</option>
            {list.baseKind !== 'OWN' && <option value="calc">Con precio calculado</option>}
            <option value="none">Sin precio</option>
            {costs && <option value="negative">Con margen negativo</option>}
          </Select>
          <span className="bf-toolbar-spacer" />
          <ViewToggle value={view} onChange={setView} />
        </Toolbar>
        <div style={{ height: 14 }} />
        {rows.isLoading ? (
          <Skeleton style={{ height: 200 }} />
        ) : shown.length === 0 ? (
          <p className="bf-cell-sub">No hay productos con ese filtro.</p>
        ) : view === 'grid' ? (
          <div className="bf-ws-cards bf-ws-cards--prices">
            {page.map((r) => (
              <article key={r.productId} className="bf-ws-card">
                <div>
                  <div className="bf-ws-card-name">{r.productName}</div>
                  <div className="bf-cell-sub">
                    {r.categoryName ?? 'Sin categoría'} · {r.unitType === 'WEIGHT' ? 'por kilo' : 'por unidad'}
                  </div>
                </div>
                <div className="bf-ws-card-money">{r.priceCents !== null ? formatMoney(r.priceCents) : 'Sin precio'}</div>
                <div className="bf-ws-card-row">
                  <span>Mostrador</span>
                  <span>{r.retailPriceCents !== null ? formatMoney(r.retailPriceCents) : '—'}</span>
                </div>
                {costs && (
                  <div className="bf-ws-card-row">
                    <span>Margen</span>
                    {margin(r)}
                  </div>
                )}
                {priceInput(r)}
              </article>
            ))}
          </div>
        ) : (
          <div className="bf-table-wrap bf-ws-compact">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Categoría</th>
                  <th style={{ textAlign: 'right' }}>Mostrador</th>
                  <th style={{ width: 160 }}>Precio propio</th>
                  <th style={{ textAlign: 'right' }}>Se usa</th>
                  {costs && <th style={{ textAlign: 'right' }}>Margen</th>}
                </tr>
              </thead>
              <tbody>
                {page.map((r) => (
                  <tr key={r.productId}>
                    <td>
                      <span className="bf-cell-main">{r.productName}</span>{' '}
                      <span className="bf-cell-sub">{r.unitType === 'WEIGHT' ? '· kg' : '· u.'}</span>
                    </td>
                    <td className="bf-cell-sub">{r.categoryName ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{r.retailPriceCents !== null ? formatMoney(r.retailPriceCents) : '—'}</td>
                    <td>{priceInput(r)}</td>
                    <td style={{ textAlign: 'right' }}>{r.priceCents !== null ? formatMoney(r.priceCents) : '—'}</td>
                    {costs && <td style={{ textAlign: 'right' }}>{margin(r)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pager}
      </CardBody>
    </Card>
  );
}

function ListDialog({
  list,
  all,
  onClose,
  onSaved,
}: {
  list: PriceListDTO | null;
  all: PriceListDTO[];
  onClose: () => void;
  onSaved: (id: string | null) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(list?.name ?? '');
  const [baseKind, setBaseKind] = useState<PriceListBase>(list?.baseKind ?? 'OWN');
  const [baseListId, setBaseListId] = useState(list?.baseListId ?? '');
  const [adjust, setAdjust] = useState(String(list?.adjustPct ?? 0).replace('.', ','));
  const save = useMutation({
    mutationFn: () => {
      const body = { name, baseKind, baseListId: baseKind === 'LIST' ? baseListId || null : null, adjustPct: Number(adjust.replace(',', '.')) || 0 };
      return list ? api.put<PriceListDTO[]>(`/wholesale/price-lists/${list.id}`, body) : api.post<PriceListDTO[]>('/wholesale/price-lists', body);
    },
    onSuccess: async (lists) => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success(list ? 'Lista actualizada' : 'Lista creada');
      onSaved(lists.find((l) => l.name.toLowerCase() === name.trim().toLowerCase())?.id ?? null);
    },
    onError: (e) => toast.error('No se pudo guardar', errorMessage(e)),
  });
  const archive = useMutation({
    mutationFn: () => api.del(`/wholesale/price-lists/${list!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      toast.success('Lista archivada');
      onSaved(null);
    },
    onError: (e) => toast.error('No se pudo archivar', errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={list ? `Editar ${list.name}` : 'Nueva lista de precios'}>
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="Nombre">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Restaurantes, Zona Norte…" />
        </Field>
        <Field label="Cómo se arma">
          <Select value={baseKind} onChange={(e) => setBaseKind(e.target.value as PriceListBase)}>
            {PRICE_LISTS_BASE_ORDER.map((k) => (
              <option key={k} value={k}>
                {PRICE_LIST_BASE_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {baseKind === 'LIST' && (
          <Field label="Lista base">
            <Select value={baseListId} onChange={(e) => setBaseListId(e.target.value)}>
              <option value="">Elegí</option>
              {all
                .filter((l) => l.id !== list?.id)
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        {baseKind !== 'OWN' && (
          <Field label="Ajuste (%)" hint="Negativo para bajar: −10 es 10 % menos. Se redondea al peso.">
            <Input value={adjust} onChange={(e) => setAdjust(e.target.value)} inputMode="decimal" />
          </Field>
        )}
        {baseKind === 'OWN' && <Callout tone="info">Después cargás el precio de cada producto en la tabla.</Callout>}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          {list ? (
            <Button variant="ghost" loading={archive.isPending} onClick={() => archive.mutate()}>
              Archivar
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={name.trim().length < 2 || (baseKind === 'LIST' && !baseListId)}
              onClick={() => save.mutate()}
            >
              Guardar
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
